import { UtcDateString } from '../common';
import { PlanValidationStatusCode, RawDataValidationError, ShortValidationResult } from './plan-service.types';
import { IRawData } from './raw-data.types';

export type ApiDraftRawData = {
    id: number,
    data: IRawData,
    changedAtUtc: UtcDateString,
    changedBy: number,
    rawDataChangedAtUtc: UtcDateString,
    validatedAt?: UtcDateString,
    validationResult?: RawDataValidationError[],
    validationShortResult?: ShortValidationResult,
    validationStatusCode: PlanValidationStatusCode,
}

export type ApiChangedAtUtcState = {
    before: UtcDateString,
    after: UtcDateString,
}

export type ApiUpdateDraftResult = {
    changedAtUtcState: ApiChangedAtUtcState,
}

export enum DraftEntityTypeCode {
    Order = 'orders',
    ProductionOrders = 'productionOrders',
    CalculationSettings = 'calculationSettings',
    WorkModeDirective = 'directive.workMode',
    Directive = 'directive',
    WorkplaceOperationBindDirective = 'directive.workplaceOperationBinding',
    AdjustmentControlDirective = 'directive.adjustmentControl',
    WorkplaceAdjustmentDirective = 'directive.workplaceAdjustment',
    BasketOperations = 'directive.basketOperations',
    SimultaneousOperations = 'directive.simultaneousOperations',
    PercentExecutionOperationsDirective = 'directive.percentExecution',
    OperationBreakDirective = 'directive.operationBreak',
    WorkplacePerformanceDirective = 'directive.workplacePerformances',
    AltRoute = 'directive.altRoute',
    AltMaterials = 'directive.altMaterials',
    KitOperationsDirective = 'directive.kit',
    MaterialMeasureUnits = 'directive.materialMeasureUnits',
    BlackBoxDirective = 'directive.blackBox',
    BatchSize = 'directive.batchSize',
    MaterialMin = 'directive.materialMin',
    Weight = 'weight',
    Matrix = 'transitionMatrix.matrix',
    TransitionGroup = 'transitionMatrix.group',
    Workplaces = 'workplaces',
    Technologies = 'technologies',
    WorkplaceSettings = 'directive.workplaceSettings',
    EndProducts = 'endProducts.data',
    Wip = 'wip.data',
    BusinessCalendar = 'businessCalendar',
    Calendar = 'calendar',
    ConsistentOperations = 'directive.consistentOperations',
    DeliveryPlan = 'deliveryPlan.data',
    Staff = 'staff',
    StaffWorkModeDirective = 'directive.staffWorkMode',
    Campaign = 'directive.campaign',
    Strategies = 'directive.strategies',
    StrategyParams = 'directive.strategyParams',
    Warehouse = 'warehouse',
    StrategyWorkplaceParams = 'directive.strategyWorkplaceParams',
    WorkplaceRestrictions = 'directive.workplaceRestrictions',
    Materials = 'materials',
}


export const DraftEntityTypeName: { [key in DraftEntityTypeCode]: string } = {
    [DraftEntityTypeCode.Order]: 'Заказ',
    [DraftEntityTypeCode.ProductionOrders]: 'Производственный заказ',
    [DraftEntityTypeCode.CalculationSettings]: 'Параметры расчёта',
    [DraftEntityTypeCode.WorkModeDirective]: 'Директива "Режимом работы рабочих мест"',
    [DraftEntityTypeCode.WorkplaceOperationBindDirective]: 'Директива "Привязка рабочего места и операции"',
    [DraftEntityTypeCode.AdjustmentControlDirective]: 'Директива "Время наладки"',
    [DraftEntityTypeCode.WorkplaceAdjustmentDirective]: 'Директива "Время наладки по умолчанию для групп оборудования"',
    [DraftEntityTypeCode.BasketOperations]: 'Директива "Садочные операции"',
    [DraftEntityTypeCode.SimultaneousOperations]: 'Директива "Одновременные операции"',
    [DraftEntityTypeCode.PercentExecutionOperationsDirective]: 'Дирпектива "Процент выполнения операции"',
    [DraftEntityTypeCode.OperationBreakDirective]: 'Директива "Управление прерываниями операций"',
    [DraftEntityTypeCode.WorkplacePerformanceDirective]: 'Директива "Производительность непрерывных операций"',
    [DraftEntityTypeCode.AltRoute]: 'Директива "Альтернативный ВИМ"',
    [DraftEntityTypeCode.KitOperationsDirective]: 'Директива "Комплектные операции"',
    [DraftEntityTypeCode.MaterialMeasureUnits]: 'Директива "Материалы с двойными единицами измерения"',
    [DraftEntityTypeCode.BatchSize]: 'Директива " Ограничение размера партии изготовления"',
    [DraftEntityTypeCode.MaterialMin]: 'Директива "Минимальный размер партии потребления"',
    [DraftEntityTypeCode.BlackBoxDirective]: 'Директива "Чёрный ящик"',
    [DraftEntityTypeCode.Weight]: 'Веса оптимизации',
    [DraftEntityTypeCode.Matrix]: 'Матрица перемещений',
    [DraftEntityTypeCode.TransitionGroup]: 'Группы перемещений',
    [DraftEntityTypeCode.Workplaces]: 'Рабочее место',
    [DraftEntityTypeCode.Technologies]: 'Технология',
    [DraftEntityTypeCode.WorkplaceSettings]: 'Директива "Характеристики рабочего места"',
    [DraftEntityTypeCode.EndProducts]: 'Готовая продукция',
    [DraftEntityTypeCode.Wip]: 'НЗП',
    [DraftEntityTypeCode.BusinessCalendar]: 'Производственный календарь',
    [DraftEntityTypeCode.Calendar]: 'График',
    [DraftEntityTypeCode.ConsistentOperations]: 'Директива "Выполнение следующей операции на том же рабочем месте"',
    [DraftEntityTypeCode.Staff]: 'Персонал',
    [DraftEntityTypeCode.StaffWorkModeDirective]: 'Директива "Управление режимом работы персонала"',
    [DraftEntityTypeCode.Campaign]: 'Директива "Размеры кампаний"',
    [DraftEntityTypeCode.Strategies]: 'Директива "Стратегии оптимизации"',
    [DraftEntityTypeCode.StrategyParams]: 'Директива "Параметры стратегий"',
    [DraftEntityTypeCode.Warehouse]: 'Склады',
    [DraftEntityTypeCode.StrategyWorkplaceParams]: 'Директива "Параметры стратегий для рабочих мест"',
    [DraftEntityTypeCode.WorkplaceRestrictions]: 'Директива "Особые ограничения рабочих мест"',
    [DraftEntityTypeCode.DeliveryPlan]: 'План поставки',
    [DraftEntityTypeCode.Materials]: 'Материалы',
};

export type ApiRawDataChangeItem = {
    entity: DraftEntityTypeCode,
    insert?: number,
    update?: number,
    delete?: number,
}

export type ApiUpdateDraftTransactionInfo = {
    apsRawDataChanges: ApiRawDataChangeItem[],
}





